import { useEffect } from "react";
import { FaBolt, FaUsers, FaClock, FaShieldAlt } from "react-icons/fa";
import "../assets/styles/LeadIAPage.css";
import BotaoContato from "../components/common/BotaoContato";
import Footer20 from "../components/common/Footer20";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */
const plans = [
  {
    name: "Piloto",
    price: "R$ 0,00",
    features: ["Configuração rápida", "2 semanas de ativação em posts selecionados", "Relatório inicial"],
    cta: "Iniciar Piloto",
    popular: false,
    color: "#16a34a",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto LeadIA no plano Piloto"
  },
  {
    name: "Essencial",
    price: "R$ 59,90/mês",
    features: ["Monitoramento contínuo", "Relatórios semanais", "Suporte básico"],
    cta: "Assinar Essencial",
    popular: true,
    color: "#FFC300",
    textColor: "#0d1b2a",
    waMessage: "Gostaria de ativar o produto LeadIA no plano Essencial"
  },
  {
    name: "Pro",
    price: "R$ 299,90/mês",
    features: ["Integração CRM", "SLA de atendimento", "Personalização de regras e templates", "Suporte prioritário"],
    cta: "Assinar Pro",
    popular: false,
    color: "#7c3aed",
    textColor: "#fff",
    waMessage: "Gostaria de ativar o produto LeadIA no plano Pro"
  },
];

const faqs = [
  { q: "Funciona em posts que aparecem no meu feed?", a: "Sim — o sistema analisa o conteúdo que aparece para o perfil do cliente e aciona quando encontra relevância." },
  { q: "Posso aprovar o tom e as mensagens?", a: "Sim — todos os templates são aprovados por você antes da ativação." },
  { q: "Como recebo os leads?", a: "Via Google Sheets/CSV ou integração com seu CRM (plano Pro)." },
  { q: "Existe risco de bloqueio?", a: "Reduzimos risco com limites, variação de texto e piloto controlado; monitoramos e ajustamos continuamente." },
];

/* ─────────────────────────────────────────
   HOOK
───────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".at-reveal");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ─────────────────────────────────────────
   COMPONENTS
───────────────────────────────────────── */
function Navbar() {
  return (
    <nav className="at-nav">
      <a href="/" className="at-nav-logo">Summer<span>Tech</span></a>
      <a href="/" className="at-nav-back">← Voltar para início</a>
      <a href="#cta" className="at-nav-cta">Ativar LeadIA →</a>
    </nav>
  );
}

function DashboardMockup() {
  return (
    <div className="at-hero-visual">
      <div className="at-dashboard">
        <div className="at-dash-bar">
          <div className="at-dash-dot" style={{ background: "#ff5f57" }} />
          <div className="at-dash-dot" style={{ background: "#febc2e" }} />
          <div className="at-dash-dot" style={{ background: "#28c840" }} />
          <span className="at-dash-title">LeadIA · Dashboard</span>
        </div>
        <div className="at-dash-body">
          <div className="at-dash-row">
            <span className="at-dash-icon">📸</span>
            <div className="at-dash-info">
              <span className="at-dash-label">Post identificado</span>
              <span className="at-dash-val">"Alguém indica serviço de ..."</span>
            </div>
            <span className="at-dash-tag at-tag-yellow">Relevante</span>
          </div>
          <div className="at-dash-row">
            <span className="at-dash-icon">💬</span>
            <div className="at-dash-info">
              <span className="at-dash-label">Comentário enviado</span>
              <span className="at-dash-val">Oi! Podemos ajudar 👋 Te mandei DM</span>
            </div>
            <span className="at-dash-tag at-tag-green">Enviado</span>
          </div>
          <div className="at-dash-row">
            <span className="at-dash-icon">🎯</span>
            <div className="at-dash-info">
              <span className="at-dash-label">Lead capturado</span>
              <span className="at-dash-val">Novo contato → CRM</span>
            </div>
            <span className="at-dash-tag at-tag-cyan">Registrado</span>
          </div>
          <div className="at-dash-stats">
            <div className="at-dash-stat">
              <span className="at-dash-stat-num">47</span>
              <span className="at-dash-stat-lbl">Leads hoje</span>
            </div>
            <div className="at-dash-stat">
              <span className="at-dash-stat-num">312</span>
              <span className="at-dash-stat-lbl">Posts monit.</span>
            </div>
            <div className="at-dash-stat">
              <span className="at-dash-stat-num">94%</span>
              <span className="at-dash-stat-lbl">Taxa resposta</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  return (
    <section className="at-hero">
      <div className="at-hero-glow" />
      <div className="at-hero-glow2" />
      <div className="at-hero-inner">
        <div className="at-hero-text">
          <span className="at-hero-badge">
            <span className="at-badge-dot" />
            Novo produto · SummerTech
          </span>
          <h1><span className="at-accent">Lead</span>IA</h1>
          <p className="at-hero-tagline">Leads do Instagram no piloto automático</p>
          <p>
            Monitora posts relevantes no feed do seu perfil, comenta com mensagens aprovadas e
            convida para DM — registrando cada lead automaticamente no seu CRM ou planilha.
          </p>
          <div className="at-hero-actions">
            <a href="#cta" className="at-btn-primary">Ativar LeadIA</a>
            <a href="#como" className="at-btn-ghost">Como funciona</a>
          </div>
        </div>
        <DashboardMockup />
      </div>
    </section>
  );
}

function SobreSection() {
  return (
    <section className="at-section at-sobre">
      <div className="at-wrap">
        <div className="at-sobre-grid at-reveal">
          <div>
            <span className="at-tag">O Produto</span>
            <h2 className="at-title">O que é LeadIA</h2>
            <p className="at-text">
              LeadIA monitora o conteúdo que aparece no feed do perfil do seu negócio, identifica
              publicações com potencial de interesse e interage automaticamente com mensagens curtas
              e aprovadas. O objetivo é iniciar conversas qualificadas e aumentar o volume de leads
              sem aumentar a equipe.
            </p>
          </div>
          <div className="at-sobre-cards">
            {[
              { icon: "🔍", title: "Monitoramento contínuo", text: "Analisa o feed em tempo real, filtrando por categorias e palavras-chave configuradas por você." },
              { icon: "💬", title: "Interação com templates aprovados", text: "Nunca envia mensagem sem a sua aprovação prévia. Controle total sobre o tom da marca." },
              { icon: "📊", title: "Registro e mensuração", text: "Cada lead é registrado com origem, horário e status — exportável a qualquer momento." },
            ].map((c) => (
              <div className="at-info-card" key={c.title}>
                <div className="at-info-icon">{c.icon}</div>
                <div>
                  <div className="at-info-title">{c.title}</div>
                  <div className="at-info-text">{c.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ComoSection() {
  const steps = [
    { icon: <FaBolt />, num: "01", title: "Monitoramento inteligente", desc: "Capturamos posts que aparecem no feed do seu perfil e avaliamos relevância por categorias e palavras-chave." },
    { icon: <FaUsers />, num: "02", title: "Interação segura", desc: "Comentamos com mensagens curtas e convidamos o usuário para DM, sempre com templates aprovados por você." },
    { icon: <FaClock />, num: "03", title: "Conversão e registro", desc: "Leads capturados são registrados e enviados ao seu time ou CRM; relatórios semanais mostram resultados." },
  ];
  return (
    <section className="at-section at-como" id="como">
      <div className="at-wrap">
        <span className="at-tag">Como Funciona</span>
        <h2 className="at-title">Três etapas.<br />Resultado automático.</h2>
        <div className="at-steps">
          {steps.map((s) => (
            <div className="at-step at-reveal" key={s.num}>
              <div className="at-step-num">{s.num}</div>
              <span className="at-step-icon">{s.icon}</span>
              <div className="at-step-title">{s.title}</div>
              <div className="at-step-desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BeneficiosSection() {
  const bens = [
    { num: "+Leads", title: "Sem aumentar equipe", text: "Escale a captação de leads sem contratar mais atendentes." },
    { num: "<1min", title: "Resposta imediata", text: "Reduza o tempo até o primeiro contato e aumente a taxa de conversão." },
    { num: "CSV", title: "Mensuração clara", text: "Relatórios semanais e exportação completa para análise." },
    { num: "100%", title: "Operação segura", text: "Limites de envio, rotação de mensagens e fallback humano configurável." },
  ];
  return (
    <section className="at-section at-beneficios">
      <div className="at-wrap">
        <span className="at-tag">Benefícios</span>
        <h2 className="at-title">Por que LeadIA<br />funciona para você</h2>
        <div className="at-ben-grid">
          {bens.map((b) => (
            <div className="at-ben-card at-reveal" key={b.title}>
              <span className="at-ben-num">{b.num}</span>
              <div className="at-ben-title">{b.title}</div>
              <div className="at-ben-text">{b.text}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SegurancaSection() {
  const items = [
    "Limites diários de envio para evitar bloqueios.",
    "Variação automática de texto — nunca a mesma mensagem.",
    "Blacklist de termos e contas configurável.",
    "Logs auditáveis de toda interação realizada.",
    "Consentimento registrado quando dados pessoais são coletados.",
  ];
  return (
    <section className="at-section at-seg">
      <div className="at-wrap">
        <div className="at-seg-inner at-reveal">
          <div className="at-seg-quote">
            "Operação segura, transparente e em conformidade — do piloto ao plano Pro."
          </div>
          <div>
            <span className="at-tag">Segurança e Compliance</span>
            <div className="at-seg-items">
              {items.map((item) => (
                <div className="at-seg-item" key={item}>
                  <FaShieldAlt className="at-seg-item-icon" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section className="at-section at-pricing">
      <div className="at-wrap">
        <span className="at-tag">Planos</span>
        <h2 className="at-title">Piloto rápido.<br />Escale depois.</h2>
        <p className="at-pricing-subtitle">
          Comece com nosso piloto gratuito de 2 semanas e valide resultados antes de qualquer compromisso.
        </p>
        <div className="at-plans">
          {plans.map((p) => (
            <article className={`at-plan at-reveal ${p.popular ? "popular" : ""}`} key={p.name}>
              {p.popular && <div className="at-plan-badge">Mais popular</div>}
              <div className="at-plan-name">{p.name}</div>
              <div className="at-plan-price">{p.price}</div>
              <ul className="at-plan-features">
                {p.features.map((f) => (
                  <li key={f}>
                    <span className="at-plan-check">✔</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <BotaoContato className="at-plan-btn" text={p.cta} mensagem={p.waMessage} style={{ backgroundColor: p.color, color: p.textColor }} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section className="at-section at-faq">
      <div className="at-wrap">
        <span className="at-tag">Dúvidas</span>
        <h2 className="at-title">Perguntas &<br />Respostas</h2>
        <div className="at-faq-grid">
          {faqs.map((f) => (
            <div className="at-faq-card at-reveal" key={f.q}>
              <div className="at-faq-q">{f.q}</div>
              <div className="at-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const ctaMessage = "Gostaria de ativar o produto LeadIA no meu perfil";
  return (
    <section className="at-section at-cta" id="cta">
      <div className="at-wrap">
        <div className="at-cta-inner">
          <h2>Ative LeadIA<br />no seu perfil hoje.</h2>
          <p>
            Comece a captar leads automaticamente com mensagens aprovadas
            e relatórios claros para acompanhar o desempenho.
          </p>
          <BotaoContato className="at-btn-dark" text="Ativar LeadIA agora →" mensagem={ctaMessage}/>
        </div>
      </div>
    </section>
  );
}

export default function LeadIAPage() {
  useReveal();

  return (
    <>
      <Navbar />
      <HeroSection />
      <SobreSection />
      <ComoSection />
      <BeneficiosSection />
      <SegurancaSection />
      <PricingSection />
      <FaqSection />
      <CtaSection />
      <Footer20 className="at-footer" voltarInicio="true" />
    </>
  );
}